import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchPlaceSuggestions } from "../utils/placesAutocomplete";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
const MAX_BRANCHES = 5;

const STOP_WORDS = new Set([
  "the","a","an","and","or","but","in","on","at","to","for","of","with","is","was","are","were",
  "be","been","has","have","had","do","did","not","it","its","this","that","they","their","very",
  "so","my","we","i","he","she","you","our","all","from","by","as","up","out","about","over","than",
  "me","us","him","her","who","what","when","where","how","just","too","also","would","could",
  "should","will","really","even","much","more","most","well","got","get","no","yes","if","am",
  "every","any","some","there","here","food","place","good","great","nice","best","bad","service",
  "staff","time","order","like","went","come","came","back","make","made","said","take","took",
  "know","written","review","rating","star","stars","visit","definitely","recommend","highly",
  "experience","overall","always","never","again","first","last","still","only","also","around",
  "didnt","wasnt","isnt","dont","wont","cant","shouldnt","wouldnt","couldnt","thats","ive",
  "youre","theyre","weve","hadnt","hasnt","wouldve","couldve","lets","heres","whats","doesnt",
  "truly","special","amazing","awesome","excellent","perfect","wonderful","terrible","horrible",
  "awful","worst","poor","absolute","quite","rather","pretty","extremely","incredibly",
  "actually","basically","literally","certainly","absolutely","completely","everything","nothing",
  "something","anything","anyone","everyone","thing","things","way","ways","day","days","night",
  "nights","trip","visit","hotel","hotels","room","rooms","stay","stayed","staying","property",
  "bangalore","bengaluru","mumbai","delhi","india","city","road","street","branch","branches",
  // pronouns / possessives missed
  "your","their","mine","ours","yours","theirs","myself","yourself","themselves","itself",
  // food / dining / restaurant words
  "food","foods","meal","meals","dish","dishes","menu","taste","tasty","delicious","yummy",
  "fresh","spicy","sweet","salty","cooked","cooking","eating","eaten","drink","drinks","juice",
  "coffee","lunch","dinner","breakfast","brunch","snack","rice","chicken","beef","bread",
  "wrap","salad","pizza","burger","burrito","sandwich","pasta","noodles","soup","curry",
  "taco","tacos","bowl","bowls","paneer","biryani","dosa","idli","roti","naan","kebab",
  "mexican","italian","chinese","indian","american","continental","thai","korean","japanese",
  "peri","masala","tikka","vinegar","ketchup","mayo","sauce","sauces","gravy","chutney",
  "fries","wings","nuggets","strips","combo","meal","value","extra","side","portion",
  "rajma","chole","jeera","panner","mattar","beans","lentils","legume",
  // too generic to be a meaningful brand keyword
  "quick","quickly","fast","slow","healthy","unhealthy","parking","customer","customers",
  "which","that","this","these","those","what","when","where","how","why","whether",
  "must","need","needs","needs","getting","giving","having","being","doing","making",
  "outlet","kitchen","express","cafe","restaurant","diner","eatery","bistro","canteen",
  // delivery platforms
  "swiggy","zomato","uber","eats","blinkit","dunzo","magicpin","dineout","yelp","tripadvisor",
  // verb past-tense forms of already-stopped words
  "ordered","visited","ordered","recommended","experienced","tasted","waited","served",
  "received","returned","enjoyed","loved","hated","liked","disliked","arrived","seated",
  // too generic as brand keywords
  "store","stores","location","locations","outlet","branch","chain","subs","ingredients","menu","item","items",
  "staffs","quality","after","before","during","delivery","deliveries","times","timing",
  "again","often","usually","sometimes","always","never","every","once","twice",
  "amount","level","type","kind","sort","part","point","issue","issues","problem","problems",
  "without","within","between","across","above","below","inside","outside","behind","front",
  "price","prices","priced","pricing","cost","costs","charge","charges","charged","paid","pay",
  "area","areas","zone","zones","table","tables","seat","seats","queue","queues","wait","waiting",
  "number","numbers","hour","hours","minute","minutes","second","seconds","week","weeks","month",
  "update","updates","change","changes","changed","fixed","broken","wrong","right","missing",
  // filler / generic english
  "california","appetite","spoilers","although","however","anyway","therefore","hence",
  "despite","because","since","while","though","unless","until","either","neither","both",
  "same","different","want","need","trying","doing","going","looking","getting","using",
  "coming","putting","giving","taking","seeing","thinking","feeling","working","telling",
  "asked","told","tried","used","seen","gone","done","given","made","been","come","find",
  "found","called","seemed","looked","started","turned","left","keep","kept",
  "long","little","number","small","large","big","high","low","old","new","next","right",
  "own","other","another","people","person","man","woman","let",
]);

function makeFranchiseStops(franchiseName) {
  return new Set(
    (franchiseName || "").toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).filter((w) => w.length > 1)
  );
}

function getTopKeywords(reviews, minRating, maxRating, n = 5, minFreq = 2, extraStops = new Set()) {
  const freq = {};
  reviews
    .filter((r) => r.rating >= minRating && r.rating <= maxRating)
    .forEach((r) =>
      (r.comment || "").toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).forEach((w) => {
        if (w.length > 3 && !STOP_WORDS.has(w) && !extraStops.has(w)) freq[w] = (freq[w] || 0) + 1;
      })
    );
  return Object.entries(freq)
    .filter(([, cnt]) => cnt >= minFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([w]) => w);
}

function getSystemicIssues(branches, extraStops = new Set()) {
  const branchNegs = branches.map((b) => new Set(getTopKeywords(b.reviews, 1, 2, 10, 1, extraStops)));
  const allWords   = new Set(branchNegs.flatMap((s) => [...s]));
  const result     = [];
  allWords.forEach((word) => {
    const count = branchNegs.filter((s) => s.has(word)).length;
    if (count >= 2) result.push({ word, count });
  });
  return result.sort((a, b) => b.count - a.count).slice(0, 6);
}

function getSystemicStrengths(branches, extraStops = new Set()) {
  const branchPos = branches.map((b) => new Set(getTopKeywords(b.reviews, 4, 5, 10, 2, extraStops)));
  const allWords  = new Set(branchPos.flatMap((s) => [...s]));
  const result    = [];
  allWords.forEach((word) => {
    const count = branchPos.filter((s) => s.has(word)).length;
    if (count >= 2) result.push({ word, count });
  });
  return result.sort((a, b) => b.count - a.count).slice(0, 6);
}

// ─── Branch Slot ───────────────────────────────────────────────
function BranchSlot({ slot, index, franchiseName, onSelect, onClear }) {
  const [mode,       setMode]       = useState("guided"); // "guided" | "manual"
  const [input,      setInput]      = useState("");
  const [candidates, setCandidates] = useState([]);
  const [showDrop,   setShowDrop]   = useState(false);
  const [activeIdx,  setActiveIdx]  = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef     = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Guided: prepend franchise name so Google returns only matching branches
  const fetchGuided = useCallback(async (q) => {
    if (!franchiseName.trim()) return;
    const query = `${franchiseName.trim()} ${q.trim()}`.trim();
    const all   = await fetchPlaceSuggestions(query);
    // Loose match: any word of franchise name appears in main_text or description
    const fWords = franchiseName.trim().toLowerCase().split(/\s+/).filter((w) => w.length > 1);
    const list  = all.filter((s) => {
      const haystack = (s.main_text + " " + s.description).toLowerCase();
      return fWords.some((w) => haystack.includes(w));
    }).map((s) => ({ data_id: s.description || s.main_text, name: s.main_text, address: s.secondary_text || "" }));
    setCandidates(list); setShowDrop(list.length > 0); setActiveIdx(-1);
  }, [franchiseName]);

  // Manual: free search, no filter — user knows the exact name on Google Maps
  const fetchManual = useCallback(async (q) => {
    const all  = await fetchPlaceSuggestions(q);
    const list = all.map((s) => ({ data_id: s.description || s.main_text, name: s.main_text, address: s.secondary_text || "" }));
    setCandidates(list); setShowDrop(list.length > 0); setActiveIdx(-1);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim().length < 2) { setCandidates([]); setShowDrop(false); return; }
      mode === "guided" ? fetchGuided(val) : fetchManual(val);
    }, 150);
  };

  const handleSelect = (c) => { setInput(""); setCandidates([]); setShowDrop(false); onSelect(index, c.name, c.data_id, c.address); };

  const handleKeyDown = (e) => {
    if (!showDrop || !candidates.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, candidates.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); handleSelect(candidates[activeIdx]); }
    else if (e.key === "Escape") setShowDrop(false);
  };

  const switchMode = (m) => { setMode(m); setInput(""); setCandidates([]); setShowDrop(false); };

  // ── Filled state ──
  if (slot.name && slot.reviews) {
    const avg     = (slot.reviews.reduce((s, r) => s + r.rating, 0) / slot.reviews.length).toFixed(1);
    const cls     = avg >= 4 ? "good" : avg >= 3 ? "avg" : "bad";
    const total   = slot.reviews.length;
    const posPct  = Math.round((slot.reviews.filter((r) => r.rating >= 4).length / total) * 100);
    const negPct  = Math.round((slot.reviews.filter((r) => r.rating <= 2).length / total) * 100);
    return (
      <div className="fv-slot fv-slot--filled">
        <button className="fv-slot-clear" onClick={() => onClear(index)} title="Remove">✕</button>
        <div className="fv-slot-label">Branch {index + 1}</div>
        <div className="fv-slot-name">{slot.name}</div>
        {slot.address && <div className="fv-slot-addr">{slot.address}</div>}
        <div className={`fv-slot-rating ${cls}`}>★ {avg}</div>
        <div className="fv-slot-count">{total} reviews</div>
        <div className="fv-slot-sentiment">
          <span className="fv-sent fv-sent--pos">{posPct}% positive</span>
          <span className="fv-sent fv-sent--neg">{negPct}% negative</span>
        </div>
      </div>
    );
  }

  if (slot.loading) return (
    <div className="fv-slot fv-slot--loading">
      <div className="fv-slot-spinner" />
      <span>Loading {slot.name}…</span>
    </div>
  );

  // ── Empty state ──
  return (
    <div className="fv-slot fv-slot--empty" ref={wrapRef}>
      <div className="fv-slot-label">Branch {index + 1}</div>
      <div className="fv-slot-plus">+</div>

      {!franchiseName.trim() ? (
        <p className="fv-slot-hint">Enter franchise name above first</p>
      ) : (
        <>
          <div className="fv-mode-tabs">
            <button className={`fv-mode-tab${mode === "guided" ? " active" : ""}`} onClick={() => switchMode("guided")}>
              Guided
            </button>
            <button className={`fv-mode-tab${mode === "manual" ? " active" : ""}`} onClick={() => switchMode("manual")}>
              Manual
            </button>
          </div>

          {mode === "guided" && (
            <p className="fv-mode-hint">Type area/city — searches <b>{franchiseName}</b> branches only</p>
          )}
          {mode === "manual" && (
            <p className="fv-mode-hint">Type exact name on Google Maps — for oddly named branches</p>
          )}

          <div className="fv-slot-input-wrap">
            <input
              className="fv-slot-input"
              placeholder={mode === "guided" ? "Type area or city…" : "Type exact branch name…"}
              value={input}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (input.trim().length >= 2) mode === "guided" ? fetchGuided(input) : fetchManual(input); }}
              autoComplete="off"
            />
            {showDrop && candidates.length > 0 && (
              <ul className="fv-ac-list">
                {candidates.map((c, i) => (
                  <li key={c.data_id || i} className={`fv-ac-item${i === activeIdx ? " active" : ""}`} onMouseDown={() => handleSelect(c)}>
                    <span className="fv-ac-name">{c.name}</span>
                    {c.address && <span className="fv-ac-addr">{c.address}</span>}
                  </li>
                ))}
              </ul>
            )}
            {showDrop && candidates.length === 0 && input.trim().length >= 2 && (
              <div className="fv-ac-empty">
                No branches found.{mode === "guided" ? <> Try <b>Manual</b> mode.</> : null}
              </div>
            )}
          </div>
        </>
      )}
      {slot.error && <span className="fv-slot-err">Branch not found</span>}
    </div>
  );
}

// ─── Welcome empty state ──────────────────────────────────────────
function FranchiseWelcome() {
  return (
    <div className="fv-welcome">
      <div className="fv-welcome-hero">
        <h2 className="fv-welcome-title">Track All Your Franchise Branches</h2>
        <p className="fv-welcome-sub">Enter your franchise name above, add branches, and get instant brand health — rankings, consistency score, systemic issues, and AI recommendations in one dashboard.</p>
      </div>

      <div className="fv-welcome-visual">
        <svg viewBox="0 0 560 210" fill="none" xmlns="http://www.w3.org/2000/svg" className="fv-welcome-svg" aria-hidden="true">
          <style>{`
            .fwv-card   { fill:#ffffff; stroke:#E0E0E0; stroke-width:1.5; }
            .fwv-elev   { fill:#F5F5F5; }
            .fwv-text   { fill:#1A1A1A; }
            .fwv-muted  { fill:#888888; }
            .fwv-dimbar { fill:#E0E0E0; }
            @media(prefers-color-scheme:dark){
              .fwv-card   { fill:#1a1a1a; stroke:#2A2A2A; }
              .fwv-elev   { fill:#252525; }
              .fwv-text   { fill:#F0F0F0; }
              .fwv-muted  { fill:#666666; }
              .fwv-dimbar { fill:#2A2A2A; }
            }
          `}</style>

          {/* ── Central HQ card ── */}
          <rect x="185" y="8" width="190" height="82" rx="12" fill="#1E293B"/>
          <text x="280" y="24" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="8" fontWeight="700" fill="#64748B" letterSpacing="1.5">FRANCHISE HQ</text>
          <text x="280" y="58" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="20" fontWeight="900" fill="#ffffff">4.5 <tspan fontSize="11" fill="#f59e0b">★</tspan></text>
          <text x="280" y="77" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7.5" fill="#475569">3 branches · Grade A · Consistent</text>

          {/* Connector lines */}
          <line x1="100" y1="108" x2="210" y2="90" stroke="#CBD5E1" strokeWidth="1.2" strokeDasharray="4 3"/>
          <line x1="280" y1="108" x2="280" y2="90" stroke="#CBD5E1" strokeWidth="1.2" strokeDasharray="4 3"/>
          <line x1="460" y1="108" x2="350" y2="90" stroke="#CBD5E1" strokeWidth="1.2" strokeDasharray="4 3"/>

          {/* ── Branch 1 — star ── */}
          <rect x="28" y="108" width="144" height="90" rx="10" className="fwv-card"/>
          <rect x="28" y="108" width="144" height="26" rx="10" className="fwv-elev"/>
          <rect x="28" y="122" width="144" height="12" className="fwv-elev"/>
          <text x="100" y="122" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7.5" fontWeight="700" className="fwv-text" opacity="0.75">Branch 1 — Koramangala</text>
          <text x="44" y="153" fontFamily="Lexend,sans-serif" fontSize="22" fontWeight="900" fill="#10b981">4.8</text>
          <text x="80" y="153" fontFamily="Lexend,sans-serif" fontSize="9" className="fwv-muted" opacity="0.8">★ 45 reviews</text>
          {[[96,0.88,"#10b981"],[72,0.72,"#10b981"],[54,0.58,"#f59e0b"]].map(([w,o,c],i) => (
            <g key={i}>
              <rect x="44" y={161+i*9} width="104" height="5" rx="2.5" className="fwv-dimbar"/>
              <rect x="44" y={161+i*9} width={w} height="5" rx="2.5" fill={c} opacity={o}/>
            </g>
          ))}
          <rect x="36" y="100" width="62" height="13" rx="6.5" fill="#10b981"/>
          <text x="67" y="110" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7" fontWeight="800" fill="#fff">TOP BRANCH</text>

          {/* ── Branch 2 — average ── */}
          <rect x="208" y="108" width="144" height="90" rx="10" className="fwv-card"/>
          <rect x="208" y="108" width="144" height="26" rx="10" className="fwv-elev"/>
          <rect x="208" y="122" width="144" height="12" className="fwv-elev"/>
          <text x="280" y="122" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7.5" fontWeight="700" className="fwv-text" opacity="0.75">Branch 2 — Whitefield</text>
          <text x="224" y="153" fontFamily="Lexend,sans-serif" fontSize="22" fontWeight="900" fill="#f59e0b">4.2</text>
          <text x="260" y="153" fontFamily="Lexend,sans-serif" fontSize="9" className="fwv-muted" opacity="0.8">★ 32 reviews</text>
          {[[70,0.8,"#f59e0b"],[52,0.65,"#f59e0b"],[40,0.5,"#ef4444"]].map(([w,o,c],i) => (
            <g key={i}>
              <rect x="224" y={161+i*9} width="104" height="5" rx="2.5" className="fwv-dimbar"/>
              <rect x="224" y={161+i*9} width={w} height="5" rx="2.5" fill={c} opacity={o}/>
            </g>
          ))}

          {/* ── Branch 3 — needs work ── */}
          <rect x="388" y="108" width="144" height="90" rx="10" className="fwv-card"/>
          <rect x="388" y="108" width="144" height="26" rx="10" className="fwv-elev"/>
          <rect x="388" y="122" width="144" height="12" className="fwv-elev"/>
          <text x="460" y="122" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7.5" fontWeight="700" className="fwv-text" opacity="0.75">Branch 3 — Electronic City</text>
          <text x="404" y="153" fontFamily="Lexend,sans-serif" fontSize="22" fontWeight="900" fill="#ef4444">3.6</text>
          <text x="440" y="153" fontFamily="Lexend,sans-serif" fontSize="9" className="fwv-muted" opacity="0.8">★ 28 reviews</text>
          {[[44,0.8,"#ef4444"],[34,0.65,"#ef4444"],[26,0.5,"#ef4444"]].map(([w,o,c],i) => (
            <g key={i}>
              <rect x="404" y={161+i*9} width="104" height="5" rx="2.5" className="fwv-dimbar"/>
              <rect x="404" y={161+i*9} width={w} height="5" rx="2.5" fill={c} opacity={o}/>
            </g>
          ))}
          <rect x="406" y="100" width="108" height="13" rx="6.5" fill="#ef4444"/>
          <text x="460" y="110" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7" fontWeight="800" fill="#fff">NEEDS ATTENTION</text>
        </svg>
      </div>

      <div className="fv-welcome-tags">
        {["Branch ranking","Brand consistency grade","Systemic issue detection","AI operations report","Star branch recognition"].map(t => (
          <span key={t} className="fv-welcome-tag">{t}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Brand Health Dashboard ───────────────────────────────────────
function BrandHealthDashboard({ filledSlots, brandAvg, bestIdx, worstIdx, consistency }) {
  const totalReviews = filledSlots.reduce((sum, s) => sum + s.reviews.length, 0);

  const gradeInfo = consistency
    ? consistency.cls === "good"
      ? { grade: "A", label: "All branches aligned",    color: "#10b981" }
      : consistency.cls === "avg"
      ? { grade: "B", label: "Minor variance detected", color: "#f59e0b" }
      : { grade: "C", label: "High variance — fix gaps", color: "#ef4444" }
    : null;

  const allReviews = filledSlots.flatMap(s => s.reviews);
  const promoters  = allReviews.filter(r => r.rating >= 4).length;
  const detractors = allReviews.filter(r => r.rating <= 2).length;
  const nps        = allReviews.length ? Math.round(((promoters - detractors) / allReviews.length) * 100) : null;
  const npsColor   = nps >= 50 ? "#10b981" : nps >= 0 ? "#f59e0b" : "#ef4444";

  const ranked = [...filledSlots]
    .map((s, i) => ({ ...s, origIdx: i, avg: s.reviews.reduce((sum, r) => sum + r.rating, 0) / s.reviews.length }))
    .sort((a, b) => b.avg - a.avg);

  return (
    <div className="fv-dashboard">
      {/* ── Metric cards ── */}
      <div className="fv-metrics-row">
        <div className="fv-metric-card">
          <div className="fv-metric-big fv-metric-big--brand">{brandAvg}<span className="fv-metric-star">★</span></div>
          <div className="fv-metric-lbl">Brand Average</div>
          <div className="fv-metric-sub">{totalReviews.toLocaleString()} reviews analysed</div>
        </div>
        <div className="fv-metric-card">
          <div className="fv-metric-big">{filledSlots.length}</div>
          <div className="fv-metric-lbl">Branches Tracked</div>
          <div className="fv-metric-sub">of {MAX_BRANCHES} max</div>
        </div>
        {nps !== null && (
          <div className="fv-metric-card">
            <div className="fv-metric-big" style={{ color: npsColor }}>{nps > 0 ? "+" : ""}{nps}</div>
            <div className="fv-metric-lbl">Brand NPS</div>
            <div className="fv-metric-sub">Promoters minus detractors</div>
          </div>
        )}
        {gradeInfo && (
          <div className="fv-metric-card">
            <div className="fv-metric-big" style={{ color: gradeInfo.color }}>{gradeInfo.grade}</div>
            <div className="fv-metric-lbl">Consistency Grade</div>
            <div className="fv-metric-sub">{gradeInfo.label}</div>
          </div>
        )}
        {bestIdx >= 0 && (
          <div className="fv-metric-card fv-metric-card--star">
            <div className="fv-metric-trophy">
              <svg viewBox="0 0 24 26" width="28" height="28" fill="none">
                <path d="M5 2h14v10a7 7 0 0 1-14 0V2z" fill="#fbbf24"/>
                <rect x="5" y="2" width="14" height="2.5" rx="0.5" fill="#fde68a"/>
                <path d="M5 5H3a2.5 2.5 0 0 0 0 5h2" fill="#f59e0b"/>
                <path d="M19 5h2a2.5 2.5 0 0 1 0 5h-2" fill="#f59e0b"/>
                <path d="M12 7.5l.9 2.8h2.9l-2.4 1.7.9 2.8L12 12.9l-2.3 1.9.9-2.8-2.4-1.7h2.9z" fill="#fff" opacity="0.85"/>
                <rect x="11" y="19" width="2" height="3" rx="0.5" fill="#d97706"/>
                <rect x="8" y="22" width="8" height="2.5" rx="1.25" fill="#b45309"/>
              </svg>
            </div>
            <div className="fv-metric-biz">{filledSlots[bestIdx].name}</div>
            <div className="fv-metric-lbl">Star Branch</div>
          </div>
        )}
      </div>

      {/* ── Branch leaderboard ── */}
      <div className="fv-leaderboard">
        <div className="fv-lb-title">Branch Performance Ranking</div>
        {ranked.map((b, rank) => {
          const pct      = (b.avg / 5) * 100;
          const barColor = b.avg >= 4 ? "#10b981" : b.avg >= 3.5 ? "#f59e0b" : "#ef4444";
          const isBest   = b.origIdx === bestIdx;
          const isWorst  = b.origIdx === worstIdx && b.origIdx !== bestIdx;
          const gap      = parseFloat((4.5 - b.avg).toFixed(1));
          return (
            <div key={b.data_id || b.name} className={`fv-lb-row${isBest ? " fv-lb-row--best" : ""}${isWorst ? " fv-lb-row--worst" : ""}`}>
              <span className="fv-lb-rank">#{rank + 1}</span>
              <div className="fv-lb-info">
                <span className="fv-lb-name">{b.name.split(",")[0]}</span>
                <div className="fv-lb-meta">
                  <span className="fv-lb-reviews">{b.reviews.length} reviews</span>
                  {gap > 0
                    ? <span className="fv-lb-gap">+{gap} to 4.5★</span>
                    : <span className="fv-lb-gap fv-lb-gap--ok">✓ On target</span>
                  }
                </div>
              </div>
              <div className="fv-lb-bar-track">
                <div className="fv-lb-bar-fill" style={{ width: `${pct}%`, background: barColor }}/>
              </div>
              <span className="fv-lb-val" style={{ color: barColor }}>★ {b.avg.toFixed(1)}</span>
              <span className="fv-lb-badge-slot">
                {isBest  && <span className="fv-lb-badge fv-lb-badge--best">Top</span>}
                {isWorst && <span className="fv-lb-badge fv-lb-badge--focus">Focus</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── AI Franchise Insights ─────────────────────────────────────
function renderFranchiseInsight(text) {
  return text.split("\n").filter(l => l.trim()).map((raw, i) => {
    const l = raw.trim();
    if (l.match(/^\*\*Section\s+\d+/i) || l.match(/^#{1,3}\s/)) {
      return <div key={i} className="fv-ai-section">{l.replace(/\*\*/g, "").replace(/^#{1,3}\s/, "")}</div>;
    }
    const numMatch = l.match(/^(\d+)\.\s+\*\*(.+?)\*\*[:.]\s*(.*)/);
    if (numMatch) {
      const bodyHtml = numMatch[3].replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return (
        <div key={i} className="fv-ai-item">
          <span className="fv-ai-item-num">{numMatch[1]}</span>
          <div>
            <strong className="fv-ai-item-title">{numMatch[2]}</strong>
            {numMatch[3] && <p className="fv-ai-item-text" dangerouslySetInnerHTML={{ __html: bodyHtml }}/>}
          </div>
        </div>
      );
    }
    const boldHtml = l.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (boldHtml !== l) return <p key={i} className="fv-ai-line" dangerouslySetInnerHTML={{ __html: boldHtml }}/>;
    return <p key={i} className="fv-ai-line">{l}</p>;
  });
}

function FranchiseInsightsPanel({ branches, token, franchiseName }) {
  const [state,  setState]  = useState({ loading: false, text: "", error: "" });
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(state.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePrint = () => {
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>Franchise Operations Report</title>
    <style>body{font-family:system-ui,sans-serif;max-width:720px;margin:40px auto;line-height:1.7;color:#1a1a1a;}
    h2{font-size:1.2rem;margin-bottom:4px;}p{margin:6px 0;}strong{font-weight:700;}
    .sec{font-size:0.85rem;font-weight:700;background:#f3f4f6;display:inline-block;padding:2px 10px;border-radius:4px;margin:12px 0 6px;}
    </style></head><body><h2>Franchise Operations Report</h2>
    <pre style="white-space:pre-wrap;font-family:inherit">${state.text.replace(/</g,"&lt;")}</pre>
    </body></html>`);
    win.document.close();
    win.print();
  };

  const generate = () => {
    setState({ loading: true, text: "", error: "" });
    const fStops = makeFranchiseStops(franchiseName);
    const payload = branches.map((b) => ({
      name:         b.name,
      avg:          (b.reviews.reduce((s, r) => s + r.rating, 0) / b.reviews.length).toFixed(1),
      pos_pct:      Math.round((b.reviews.filter((r) => r.rating >= 4).length / b.reviews.length) * 100),
      neg_pct:      Math.round((b.reviews.filter((r) => r.rating <= 2).length / b.reviews.length) * 100),
      review_count: b.reviews.length,
      pos_keywords: getTopKeywords(b.reviews, 4, 5, 5, 2, fStops),
      neg_keywords: getTopKeywords(b.reviews, 1, 2, 5, 2, fStops),
    }));
    fetch(`${API}/franchise/insights`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ businesses: payload }),
    })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => setState({ loading: false, text: d.insights, error: "" }))
      .catch(() => setState({ loading: false, text: "", error: "Could not generate insights. Try again." }));
  };

  return (
    <div className="fv-ai-panel">
      <div className="fv-ai-header">
        <div>
          <span className="fv-ai-title">Franchise Operations Report</span>
          <div className="fv-ai-sub">Brand health · systemic issues · per-branch action plan</div>
        </div>
        <div className="fv-ai-actions">
          {!state.text && (
            <button className="fv-ai-btn" onClick={generate} disabled={state.loading}>
              {state.loading ? <><span className="fv-ai-spinner"/> Analysing…</> : "Generate Report"}
            </button>
          )}
          {state.text && (
            <>
              <button className="fv-ai-icon-btn" onClick={handleCopy} title="Copy report">
                {copied
                  ? <svg viewBox="0 0 20 20" fill="none" width="17" height="17"><path d="M4 10.5l4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <svg viewBox="0 0 20 20" fill="none" width="17" height="17"><rect x="7" y="3" width="10" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M5 6H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                }
                <span>{copied ? "Copied!" : "Copy"}</span>
              </button>
              <button className="fv-ai-icon-btn" onClick={handlePrint} title="Print report">
                <svg viewBox="0 0 20 20" fill="none" width="17" height="17"><rect x="4" y="7" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><path d="M7 7V4h6v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M7 13h6M7 11h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><circle cx="15" cy="10" r="1" fill="currentColor"/></svg>
                <span>Print</span>
              </button>
            </>
          )}
        </div>
      </div>
      {state.error && <p className="fv-ai-error">{state.error}</p>}
      {state.text && <div className="fv-ai-body">{renderFranchiseInsight(state.text)}</div>}
      {!state.text && !state.loading && !state.error && (
        <p className="fv-ai-hint">Get a franchise operations report — brand health, systemic problems, and specific actions for each branch.</p>
      )}
    </div>
  );
}

// ─── Main FranchiseView ────────────────────────────────────────
function FranchiseView({ onExit }) {
  const { token } = useAuth();
  const [franchiseName, setFranchiseName] = useState("");
  const [slotCount,     setSlotCount]     = useState(2);
  const [slots, setSlots] = useState(
    Array.from({ length: MAX_BRANCHES }, () => ({
      name: "", address: "", data_id: "", reviews: null, loading: false, error: "",
    }))
  );

  const updateSlot = (i, updates) =>
    setSlots((prev) => { const n = [...prev]; n[i] = { ...n[i], ...updates }; return n; });

  const loadBranch = (i, name, data_id, address) => {
    updateSlot(i, { name, address, data_id, loading: true, error: "", reviews: null });
    fetch(`${API}/reviews/${encodeURIComponent(data_id || name)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => updateSlot(i, { reviews: d.reviews || [], loading: false }))
      .catch(() => updateSlot(i, { name: "", address: "", data_id: "", reviews: null, loading: false, error: "failed" }));
  };

  const clearSlot = (i) => updateSlot(i, { name: "", address: "", data_id: "", reviews: null, loading: false, error: "" });

  const resetAll = () => {
    setFranchiseName("");
    setSlotCount(2);
    setSlots(Array.from({ length: MAX_BRANCHES }, () => ({ name: "", address: "", data_id: "", reviews: null, loading: false, error: "" })));
  };

  const filledSlots  = slots.slice(0, slotCount).filter((s) => s.reviews && s.reviews.length > 0);
  const allRatings   = filledSlots.map((s) => s.reviews.reduce((sum, r) => sum + r.rating, 0) / s.reviews.length);
  const brandAvg     = allRatings.length ? (allRatings.reduce((a, b) => a + b, 0) / allRatings.length).toFixed(1) : null;
  const bestIdx      = allRatings.length ? allRatings.indexOf(Math.max(...allRatings)) : -1;
  const worstIdx     = allRatings.length > 1 ? allRatings.indexOf(Math.min(...allRatings)) : -1;
  const franchiseStops = makeFranchiseStops(franchiseName);
  const systemicIssues    = filledSlots.length >= 2 ? getSystemicIssues(filledSlots, franchiseStops) : [];
  const systemicStrengths = filledSlots.length >= 2 ? getSystemicStrengths(filledSlots, franchiseStops) : [];

  const consistency = useMemo(() => {
    if (allRatings.length < 2) return null;
    const mean     = allRatings.reduce((a, b) => a + b, 0) / allRatings.length;
    const std      = Math.sqrt(allRatings.reduce((s, r) => s + Math.pow(r - mean, 2), 0) / allRatings.length);
    if (std < 0.3) return { label: "Consistent ✓", cls: "good" };
    if (std < 0.6) return { label: "Moderate variance", cls: "avg" };
    return { label: "Inconsistent ⚠", cls: "bad" };
  }, [allRatings]);

  return (
    <div className="fv-container">
      <div className="fv-header">
        <div>
          <div className="fv-title">Franchise Branch Tracker</div>
          <div className="fv-subtitle">Track and compare your branch performance</div>
        </div>
        <button className="fv-exit-btn" onClick={onExit}>Exit</button>
      </div>

      {/* Franchise name */}
      <div className="fv-brand-row">
        <label className="fv-brand-label">Franchise name</label>
        <div className="fv-brand-input-wrap">
          <input
            className="fv-brand-input"
            placeholder="e.g. Nellai Muthu Vilas, KFC, Subway…"
            value={franchiseName}
            onChange={(e) => { setFranchiseName(e.target.value); }}
          />
          <button className="fv-reset-btn" onClick={resetAll} title="Clear all and start new search">
            <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
              <path d="M4 10a6 6 0 1 0 1.5-3.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M4 5.5V10h4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <span className="fv-brand-hint">Guided searches are locked to this brand name</span>
      </div>

      {/* Branch slots */}
      <div className="fv-slots-grid">
        {slots.slice(0, slotCount).map((slot, i) => (
          <BranchSlot
            key={i}
            slot={slot}
            index={i}
            franchiseName={franchiseName}
            token={token}
            onSelect={loadBranch}
            onClear={clearSlot}
          />
        ))}
        {slotCount < MAX_BRANCHES && (
          <button className="fv-add-btn" onClick={() => setSlotCount((n) => n + 1)}>
            + Add Branch
          </button>
        )}
      </div>

      {/* Welcome / empty state */}
      {filledSlots.length === 0 && <FranchiseWelcome />}

      {/* Brand health dashboard */}
      {filledSlots.length >= 2 && (
        <BrandHealthDashboard
          filledSlots={filledSlots}
          brandAvg={brandAvg}
          bestIdx={bestIdx}
          worstIdx={worstIdx}
          consistency={consistency}
        />
      )}

      {/* Brand strengths */}
      {systemicStrengths.length > 0 && (() => {
        const words = systemicStrengths.map(s => s.word).slice(0, 4);
        const last  = words[words.length - 1];
        const rest  = words.slice(0, -1).join(", ");
        const sent  = words.length === 1
          ? `Customers consistently praise the ${words[0]} across all your branches.`
          : `Customers consistently praise the ${rest} and ${last} across all your branches.`;
        return (
          <div className="fv-strengths">
            <div className="fv-sec-icon">
              <svg viewBox="0 0 36 36" fill="none" width="36" height="36">
                <circle cx="18" cy="18" r="16" stroke="#10b981" strokeWidth="2"/>
                <path d="M11 18.5l4.5 4.5 9-9" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="fv-sec-body">
              <div className="fv-str-title">Brand Strengths <span className="fv-str-note">— consistently praised across branches</span></div>
              <p className="fv-str-sent">{sent}</p>
              <p className="fv-str-msg">These are your brand's consistent wins — protect and amplify them in every branch.</p>
            </div>
          </div>
        );
      })()}

      {/* Systemic issues */}
      {systemicIssues.length > 0 && (() => {
        const words = systemicIssues.map(s => s.word).slice(0, 4);
        const last  = words[words.length - 1];
        const rest  = words.slice(0, -1).join(", ");
        const sent  = words.length === 1
          ? `Multiple branches face recurring complaints about ${words[0]}.`
          : `Customers across branches commonly raise concerns about ${rest} and ${last}.`;
        return (
          <div className="fv-systemic">
            <div className="fv-sec-icon">
              <svg viewBox="0 0 36 36" fill="none" width="36" height="36">
                <circle cx="18" cy="18" r="16" stroke="#f59e0b" strokeWidth="2"/>
                <path d="M18 11v8M18 23v1.5" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="fv-sec-body">
              <div className="fv-systemic-title">
                Brand-Level Issues
                <span className="fv-systemic-note"> — appearing across multiple branches</span>
              </div>
              <p className="fv-sys-sent">{sent}</p>
              <p className="fv-systemic-msg">These are not isolated issues — fix them at the brand level, not branch by branch.</p>
            </div>
          </div>
        );
      })()}

      {/* AI Report */}
      {filledSlots.length >= 2 && <FranchiseInsightsPanel branches={filledSlots} token={token} franchiseName={franchiseName} />}
    </div>
  );
}

export default FranchiseView;
